import { host } from '@config/config.js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  AUTH_STORAGE_KEYS,
  clearStoredSession,
  readStoredSession,
} from '../utils/auth-session';

const SESSION_STATUS = {
  GUEST: 'guest',
  CHECKING: 'checking',
  AUTHENTICATED: 'authenticated',
};

export const useAuthStore = defineStore('auth', () => {
  const account = ref('');
  const username = ref('');
  const token = ref('');
  const refreshToken = ref('');
  const error = ref('');
  const status = ref(SESSION_STATUS.GUEST);

  const isLoggedIn = computed(
    () =>
      status.value === SESSION_STATUS.AUTHENTICATED &&
      !!token.value &&
      !!account.value
  );
  const isChecking = computed(() => status.value === SESSION_STATUS.CHECKING);

  let sessionCheckPromise = null;
  let refreshPromise = null;

  function persistSession(data) {
    const nextToken = data.token || data.accessToken;

    if (!nextToken) {
      throw new Error('登录响应缺少访问令牌');
    }

    token.value = nextToken;
    account.value = data.account || account.value;
    username.value = data.username || username.value;

    if (data.refreshToken) {
      refreshToken.value = data.refreshToken;
    }

    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, nextToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.account, account.value);
    localStorage.setItem(AUTH_STORAGE_KEYS.username, username.value);

    if (refreshToken.value) {
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken.value);
    }

    status.value = SESSION_STATUS.AUTHENTICATED;
  }

  function clearSessionState() {
    account.value = '';
    username.value = '';
    token.value = '';
    refreshToken.value = '';
    clearStoredSession();
  }

  // 从 localStorage 恢复登录状态
  function loadFromStorage() {
    const stored = readStoredSession();

    if (stored.account && stored.accessToken) {
      account.value = stored.account;
      username.value = stored.username || stored.account;
      token.value = stored.accessToken;
      refreshToken.value = stored.refreshToken;
      status.value = SESSION_STATUS.CHECKING;
    } else {
      clearSessionState();
      status.value = SESSION_STATUS.GUEST;
    }
  }

  // 初始化时加载存储的状态
  loadFromStorage();

  async function login(acc, pwd) {
    error.value = '';

    try {
      const response = await fetch(`${host}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ account: acc, password: pwd }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload.success) {
        error.value = payload.error || '登录失败';
        return false;
      }

      persistSession(payload.data);
      return true;
    } catch (e) {
      error.value = e.message || '登录失败';
      return false;
    }
  }

  function logout() {
    clearSessionState();
    status.value = SESSION_STATUS.GUEST;
  }

  async function doRefreshAccessToken() {
    const stored = readStoredSession();
    const currentRefreshToken = refreshToken.value || stored.refreshToken;

    if (!currentRefreshToken) {
      logout();
      const error = new Error('登录已过期，请重新登录');
      error.code = 'REFRESH_FAILED';
      throw error;
    }

    let response;
    try {
      response = await fetch(`${host}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });
    } catch (_networkError) {
      const error = new Error('网络异常，暂时无法刷新登录状态');
      error.code = 'NETWORK_ERROR';
      throw error;
    }

    const payload = await response.json().catch(() => ({}));

    if (!response.ok || !payload.success) {
      const error = new Error(payload.error || '登录已过期，请重新登录');
      error.code = payload.code || 'REFRESH_FAILED';

      if (response.status === 401) {
        logout();
      }
      throw error;
    }

    persistSession(payload.data);
    return true;
  }

  async function refreshAccessToken() {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = doRefreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  }

  async function ensureValidSession() {
    if (isLoggedIn.value) {
      return true;
    }

    const stored = readStoredSession();
    if (!stored.account || !stored.accessToken) {
      if (status.value !== SESSION_STATUS.GUEST) {
        logout();
      }
      return false;
    }

    if (sessionCheckPromise) {
      return sessionCheckPromise;
    }

    status.value = SESSION_STATUS.CHECKING;
    sessionCheckPromise = (async () => {
      try {
        const session = readStoredSession();
        const response = await fetch(`${host}/api/auth/verify`, {
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
          },
        });
        const payload = await response.json().catch(() => ({}));

        if (response.ok && payload.success) {
          persistSession({
            token: session.accessToken,
            refreshToken: session.refreshToken,
            account: payload.data.account || session.account,
            username: payload.data.username || session.username,
          });
          return true;
        }

        if (response.status === 401) {
          if (!session.refreshToken) {
            logout();
            return false;
          }

          try {
            await refreshAccessToken();
            return true;
          } catch (_refreshError) {
            status.value = SESSION_STATUS.GUEST;
            return false;
          }
        }

        logout();
        return false;
      } catch (_checkError) {
        // 网络或服务异常时不清理本地令牌，但先不展示登录态。
        status.value = SESSION_STATUS.GUEST;
        return false;
      }
    })().finally(() => {
      sessionCheckPromise = null;
    });

    return sessionCheckPromise;
  }

  // 获取认证头
  function getAuthHeader() {
    if (!token.value) return {};
    return {
      Authorization: `Bearer ${token.value}`,
    };
  }

  return {
    account,
    username,
    token,
    refreshToken,
    error,
    status,
    isLoggedIn,
    isChecking,
    login,
    logout,
    ensureValidSession,
    refreshAccessToken,
    getAuthHeader,
    loadFromStorage,
  };
});
